/**
 * lazadaCache — the last good Lazada dataset, kept for the next mount.
 *
 * tiktokCache's twin, and needed for the same reason: a screen whose every
 * mount starts EMPTY and blocks first paint on a network read makes the back
 * button feel broken. Both sources here are slow for different reasons — the
 * live one is a signed paged walk at Lazada's pace, the stored one is the
 * whole order history out of SharePoint.
 *
 *   FRESH (under REVALIDATE_AFTER_MS)  paint it and read NOTHING.
 *   USABLE (under MAX_AGE_MS)          paint it at once, re-read behind it,
 *                                      settle over the top. The screen says so.
 *   OLDER, or a different key          ignored; the load runs cold.
 *
 * MEMORY ONLY, NEVER DISK. The stored half carries no contact detail by
 * construction and Lazada masks the live half before it reaches us, so this is
 * the one channel where disk would arguably be safe — and it still is not
 * taken, because "arguably safe today" is the reasoning that ages badly when
 * somebody adds a field. The rule is the same for all three channels.
 *
 * SYNCING CLEARS IT: a sync is the one moment the stored answer is known to
 * have moved, and serving the pre-sync copy would show somebody their own
 * write not having happened.
 */
import type { ILazadaStoredOrder } from "../../lib/sp/LazadaOrdersService";

/** Younger than this and the mount reads nothing at all. */
export const REVALIDATE_AFTER_MS = 60_000;
/** Older than this and it is not worth painting — the load runs cold. */
export const MAX_AGE_MS = 10 * 60_000;

export type LazadaSource = "stored" | "live";

export interface LazadaDataset {
  orders: ILazadaStoredOrder[];
  /** ISO. For Stored this is the last SYNC, never now — saying "as of now"
   *  would claim a freshness the rows do not have. */
  asOf: string;
  syncedBy: string;
  /** The live walk stopped on its time budget; there is more behind it. */
  truncated: boolean;
  /** The list is not provisioned yet — a state, not an error. */
  missing: boolean;
}

interface Held { key: string; data: LazadaDataset; at: number }

let held: Held | null = null;

export function lazadaCacheKey(source: LazadaSource, days: number): string {
  return `${source}|${days}`;
}

/** The last good dataset for this key, if it is worth painting. */
export function readLazadaCache(
  key: string, now = Date.now(),
): { data: LazadaDataset; age: number } | null {
  if (!held || held.key !== key) return null;
  const age = now - held.at;
  return age < MAX_AGE_MS ? { data: held.data, age } : null;
}

export function writeLazadaCache(key: string, data: LazadaDataset, now = Date.now()): void {
  held = { key, data, at: now };
}

export function clearLazadaCache(): void {
  held = null;
}
